import { ButtonBase } from "@mui/material";
import { captureException } from "@sentry/react";
import {
  AVAILABILITY_REPORT_STATUS_TO_BADGE_TONE_MAP,
  OutOfAreaActivationMembersSectionKeys,
  formatAvailabilityStatusLabel,
  formatDate,
  formatDateTime,
  formatMemberFullName,
  formatOutOfAreaActivationRoleCategory,
  useGetCanViewOutOfAreaActivationRequestHistory,
  useGetGroupedOutOfAreaActivationRoles,
  useOutOfAreaActivationSelectedState,
  useUpdateEffect,
} from "@ses-mams/react-utils";
import {
  OutOfAreaActivationRequestMember,
  OutOfAreaActivationRoleCategory,
} from "@ses-mams/api-contract";
import { Fragment, useCallback, useState } from "react";
import { RenderSectionHeaderParams, SectionHeader } from "~/components/ui/list";
import { Stack } from "~/components/ui/stack";
import { Checkbox } from "~/components/ui/checkbox";
import { Text } from "~/components/ui/text";
import { useAuth } from "~/context/auth";
import { DrawerContent, DrawerHeader } from "~/components/ui/drawer";
import {
  OutOfAreaActivationMemberList,
  useOutOfAreaActivationMembersData,
} from "../OutOfAreaActivationMemberList";
import { ManageOOAAMembersFooter } from "./ManageOOAAMembersFooter";
import { ManageOOAAMembersFilters } from "./ManageOOAAMembersFilters";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import { MemberConditionalReason } from "~/components/common/memberStatus";
import { PreApprovedOOAAMembersDrawer } from "./PreApprovedOOAAMembersDrawer";
import { MemberDetailsDrawer } from "~/components/common/memberDetails";
import { IconButton } from "~/components/ui/button";
import { InfoCircleFilledIcon, PlusOutlineIcon } from "~/components/ui/icon";
import { OOAARequestHistoryDrawer } from "../OOAARequestHistoryDrawer";
import { useCanEditOOAARequest } from "../../hooks/useCanEditOOAARequest";
import { ApprovalActionDrawer } from "~/pages/outOfAreaActivationApprovals/components/OutOfAreaApproval";
import { getUpdatedPagedData } from "@ses-mams/react-utils";
import { useToast } from "~/components/ui/toast";
import { useShowConflictConfirmationDialog } from "~/hooks/useShowConflictConfirmationDialog";
import { tsr } from "~/utils/client";

type ManageOOAAMembersProps = {
  isClosed: boolean;
  activationId: string;
  isDrawerOpen: boolean;
  onClose: () => void;
  onAdd?: () => void;
};

export const ManageOOAAMembers = ({
  isDrawerOpen,
  isClosed,
  activationId,
  onClose,
  onAdd,
}: ManageOOAAMembersProps) => {
  const [memberDetailsId, setMemberDetailsId] = useState<string>();
  const [memberHistoryRequestId, setMemberHistoryRequestId] =
    useState<string>();

  const [isPreApprovedMembersDrawerOpen, setIsPreApprovedMembersDrawerOpen] =
    useState(false);

  const [isApproveWithChangesDrawerOpen, setIsApproveWithChangesDrawerOpen] =
    useState(false);

  const canAmend = useCanEditOOAARequest(activationId);

  const {
    sections,
    isLoading: isLoadingMembers,
    fetchNextPage,
    hasNextPage,
  } = useOutOfAreaActivationMembersData(activationId, isDrawerOpen);

  const {
    selectedRequestIds,
    selectedActivationRequestIds,
    selectedDeactivationRequestIds,
    selectedRemovalMemberIds,
    selectedUnsuccessfulMemberIds,
    selectAllState,
    setSelected,
    clearSelected,
    setSelectAllState,
  } = useOutOfAreaActivationSelectedState();

  const { activate } = useActivateOutOfAreaMembers({
    activationId,
    selectedActivationRequestIds,
    onSuccess: clearSelected,
  });

  const handleActivateClick = useCallback(() => {
    activate(false);
    setIsApproveWithChangesDrawerOpen(false);
  }, [activate]);

  const { member } = useAuth();
  const { getCanViewOutOfAreaActivationRequestHistory } =
    useGetCanViewOutOfAreaActivationRequestHistory(member);

  useUpdateEffect(() => {
    if (!isDrawerOpen) {
      clearSelected();
    }
  }, [isDrawerOpen]);

  const renderSectionHeader = useCallback(
    ({
      title,
      data,
    }: RenderSectionHeaderParams<OutOfAreaActivationRequestMember>) => {
      const typedKey = title as OutOfAreaActivationMembersSectionKeys;

      const isSelectAllChecked =
        selectAllState[typedKey] &&
        data.every(m => selectedRequestIds.has(m.request.id));

      return (
        <ButtonBase
          disabled={!canAmend}
          onClick={() => {
            setSelected(prev => {
              const prevIds = new Set(prev.map(m => m.request.id));

              const hasUnselected = data.some(m => !prevIds.has(m.request.id));

              setSelectAllState(prevState => ({
                ...prevState,
                [typedKey]: hasUnselected,
              }));

              if (hasUnselected) {
                return [...prev, ...data];
              }

              const dataRequestIds = new Set(data.map(d => d.request.id));

              return prev.filter(p => !dataRequestIds.has(p.request.id));
            });
          }}
          sx={{ width: "100%" }}
        >
          <SectionHeader>
            <Stack direction="row" gap="small">
              {canAmend && <Checkbox checked={isSelectAllChecked} />}

              {title}
            </Stack>
          </SectionHeader>
        </ButtonBase>
      );
    },
    [canAmend, selectAllState, selectedRequestIds]
  );

  const getGroupedOutOfAreaActivationRoles =
    useGetGroupedOutOfAreaActivationRoles();

  const renderItem = useCallback(
    (item: OutOfAreaActivationRequestMember) => {
      const { request, statusUpdatedAt, mostRecentHistoryItem } = item;
      const {
        member,
        availabilityBlocks: appliedAvailabilityBlocks,
        roles,
      } = request;

      const isActivated = mostRecentHistoryItem?.action === "Activated";

      const isChecked = selectedRequestIds.has(request.id);

      const canViewHistory =
        getCanViewOutOfAreaActivationRequestHistory(request);

      const rolesByCategory = getGroupedOutOfAreaActivationRoles(
        isActivated ? mostRecentHistoryItem.roles : roles
      );

      const availabilityBlocks = isActivated
        ? mostRecentHistoryItem.availabilityBlocks
        : appliedAvailabilityBlocks;

      return (
        <Stack
          flex={1}
          direction="row"
          gap="xxsmall"
          align="center"
          paddingX="large"
        >
          <ButtonBase
            disabled={!canAmend}
            sx={{
              width: "100%",
            }}
            onClick={() => {
              if (isChecked) {
                setSelected(prev =>
                  prev.filter(p => p.request.id !== request.id)
                );
                return;
              }

              setSelected(prev => [...prev, item]);
            }}
          >
            <Stack flex={1} direction="row" gap="small">
              {canAmend ? <Checkbox checked={isChecked} /> : null}

              <Stack flex={1} gap="small" align="start" paddingY="large">
                {request.unsuccessful ? (
                  <Badge tone="cautionMuted" size="small">
                    Unsuccessful
                  </Badge>
                ) : null}

                <Text align="start" tone="info" weight="semi">
                  {formatMemberFullName(member)}
                </Text>
                <Text align="start" tone="secondary">
                  {formatDateTime(new Date(statusUpdatedAt), "long")}
                </Text>

                {member.units.length ? (
                  <Stack wrap gap="medium" direction="row">
                    {member.units.map(unit => (
                      <Text
                        key={`${member.id}-${unit.id}`}
                        align="start"
                        size="small"
                        weight="medium"
                        tone="secondary"
                      >{`${unit.name}${unit.code ? ` (${unit.code})` : ""}`}</Text>
                    ))}
                  </Stack>
                ) : null}

                {rolesByCategory && (
                  <Stack gap="small">
                    {Object.keys(rolesByCategory).map(category => {
                      const roles =
                        rolesByCategory[
                          category as keyof typeof rolesByCategory
                        ];

                      return (
                        <Stack
                          flex={1}
                          key={`${member.id}-${category}`}
                          gap="xxsmall"
                        >
                          <Text align="start" weight="medium">
                            {formatOutOfAreaActivationRoleCategory(
                              category as OutOfAreaActivationRoleCategory
                            )}
                          </Text>
                          <Text
                            align="start"
                            tone="secondary"
                            size="small"
                            weight="medium"
                          >
                            {roles.map(({ name }) => name).join(", ")}
                          </Text>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}

                {availabilityBlocks.map(block => (
                  <Fragment key={block.id}>
                    <Stack
                      gap="small"
                      direction="row"
                      justify="space-between"
                      align="center"
                      sx={{ width: "100%" }}
                    >
                      <Text
                        align="start"
                        size="small"
                      >{`${formatDate(new Date(block.start))} - ${formatDate(
                        new Date(block.end)
                      )}`}</Text>

                      {block.availabilityStatus && (
                        <Badge
                          tone={
                            AVAILABILITY_REPORT_STATUS_TO_BADGE_TONE_MAP[
                              block.availabilityStatus
                            ]
                          }
                        >
                          {formatAvailabilityStatusLabel({
                            status: block.availabilityStatus,
                            useShort: true,
                          })}
                        </Badge>
                      )}
                    </Stack>

                    {block.availabilityStatus === "Conditional" &&
                      block.conditionalReason && (
                        <MemberConditionalReason
                          conditionalReason={block.conditionalReason}
                        />
                      )}
                  </Fragment>
                ))}
              </Stack>
            </Stack>
          </ButtonBase>

          <Stack gap="medium" align="center">
            {canViewHistory && (
              <Button
                size="small"
                variant="link"
                onClick={() => setMemberHistoryRequestId(request.id)}
              >
                History
              </Button>
            )}

            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();

                setMemberDetailsId(member.id);
              }}
            >
              <InfoCircleFilledIcon tone="info" size="xsmall" />
            </IconButton>
          </Stack>
        </Stack>
      );
    },
    [
      canAmend,
      selectedRequestIds,
      getCanViewOutOfAreaActivationRequestHistory,
      getGroupedOutOfAreaActivationRoles,
    ]
  );

  return (
    <>
      <DrawerHeader onClose={onClose}>Members</DrawerHeader>

      <DrawerContent sx={{ paddingX: 0 }}>
        {!isLoadingMembers && (
          <Stack gap="medium" paddingX="large">
            {canAmend && (
              <Button
                variant="tertiary"
                startIcon={PlusOutlineIcon}
                onClick={onAdd}
              >
                Add Members
              </Button>
            )}

            <Stack
              direction="row-reverse"
              justify="space-between"
              gap="xsmall"
              paddingBottom="medium"
            >
              {canAmend && (
                <Button
                  variant="link"
                  onClick={() => setIsPreApprovedMembersDrawerOpen(true)}
                >
                  Pre-approved members
                </Button>
              )}
              <ManageOOAAMembersFilters />
            </Stack>
          </Stack>
        )}
        <OutOfAreaActivationMemberList
          isLoading={isLoadingMembers}
          sections={sections}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
        />
      </DrawerContent>

      <ManageOOAAMembersFooter
        activationId={activationId}
        selectedActivationRequestIds={selectedActivationRequestIds}
        selectedDeactivationRequestIds={selectedDeactivationRequestIds}
        selectedRemovalMemberIds={selectedRemovalMemberIds}
        selectedUnsuccessfulMemberIds={selectedUnsuccessfulMemberIds}
        isClosed={isClosed}
        clearSelectedMembers={clearSelected}
        setIsApproveWithChangesDrawerOpen={setIsApproveWithChangesDrawerOpen}
      />

      <PreApprovedOOAAMembersDrawer
        activationId={activationId}
        open={isPreApprovedMembersDrawerOpen}
        onClose={() => setIsPreApprovedMembersDrawerOpen(false)}
      />

      <ApprovalActionDrawer
        activationId={activationId}
        requestIds={selectedActivationRequestIds}
        open={isApproveWithChangesDrawerOpen}
        onClose={() => setIsApproveWithChangesDrawerOpen(false)}
        onSuccess={() => setIsApproveWithChangesDrawerOpen(false)}
        approveWithChangesLink={`/requests/out-of-area-activations/${activationId}/roles`}
        onApproveClick={handleActivateClick}
        approveLabel={"Activate"}
        declineLabel={null}
        approveWithChangesLabel={"Activate with changes"}
        approveWithChangesDisabled={selectedActivationRequestIds.length > 1}
      />

      {memberDetailsId && (
        <MemberDetailsDrawer
          open={Boolean(memberDetailsId)}
          memberId={memberDetailsId}
          onClose={() => setMemberDetailsId(undefined)}
        />
      )}

      {memberHistoryRequestId && (
        <OOAARequestHistoryDrawer
          open={Boolean(memberHistoryRequestId)}
          requestId={memberHistoryRequestId}
          onClose={() => setMemberHistoryRequestId(undefined)}
        />
      )}
    </>
  );
};

type UseActivateOutOfAreaMembersParams = {
  activationId: string;
  selectedActivationRequestIds: Array<string>;
  onSuccess: () => void;
};

const useActivateOutOfAreaMembers = ({
  activationId,
  selectedActivationRequestIds,
  onSuccess,
}: UseActivateOutOfAreaMembersParams) => {
  const queryClient = tsr.useQueryClient();

  const { mutateAsync, isPending } =
    tsr.outOfAreaActivations.activate.useMutation();

  const { addToast } = useToast();

  const { showConflictConfirmationDialog } = useShowConflictConfirmationDialog({
    type: "activation",
  });

  const activate = useCallback(
    async (force = false) => {
      try {
        const result = await mutateAsync({
          params: {
            activationId,
          },
          body: {
            requestIds: selectedActivationRequestIds,
            force,
          },
        });

        queryClient.setQueriesData(
          {
            queryKey: [
              "out-of-area-activation",
              { activationId },
              "members-v2",
            ],
          },
          getUpdatedPagedData(result.body)
        );

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              "out-of-area-activation-requests",
              "details",
              { activationId },
            ],
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: ["out-of-area-activation-requests", "list"],
          }),
        ]);

        onSuccess();
      } catch (error) {
        const apiError = error as any;
        if (apiError?.status === 422) {
          showConflictConfirmationDialog({
            text: apiError.body.message,
            onConfirm: () => activate(true),
          });
          return;
        }

        captureException(error);

        addToast({
          tone: "critical",
          title: "Sorry, something went wrong",
          message: "Please try again",
        });
      }
    },
    [activationId, selectedActivationRequestIds, onSuccess]
  );

  return {
    activate,
    isPending,
  };
};
